import { useState, useEffect } from 'react'
import { useParams as useParamsRaw, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, ArrowLeft } from 'lucide-react'
import { reportApi } from '@/lib/api'
import { 
  BarChart3, TrendingUp, Users, Hash, Globe, MapPin, Layers, Award, Star
} from 'lucide-react'

export function ReportDetailPage() {
  const params = useParamsRaw({ strict: false }) as { id?: string }
  const id = params.id || ''
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [report, setReport] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    reportApi.detail(id).then((res: any) => {
      setReport(res?.data || res)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const sections = [
    { value: 'overview', icon: BarChart3, label: t('report.dataOverview'), desc: '文章总量、资讯数量、社交数量等概览数据' },
    { value: 'emotion', icon: TrendingUp, label: t('report.emotionAnalysis'), desc: '正面/中性/负面情感占比分析' },
    { value: 'events', icon: Star, label: t('report.hotEventRanking'), desc: '热点事件排行榜' },
    { value: 'media', icon: Globe, label: t('report.mediaActivity'), desc: '媒体活跃度分析' },
    { value: 'selfmedia', icon: Award, label: t('report.selfMediaRanking'), desc: '自媒体热度排名' },
    { value: 'words', icon: Hash, label: t('report.highWordIndex'), desc: '高频词指数分析' },
    { value: 'spots', icon: MapPin, label: t('report.hotSpotRanking'), desc: '热点地区排名' },
    { value: 'netizen', icon: Users, label: t('report.netizenWordCloud'), desc: '网民高频词云图' },
    { value: 'mediaCloud', icon: Hash, label: t('report.mediaWordCloud'), desc: '媒体高频词云图' },
    { value: 'people', icon: Users, label: t('report.hotPeople'), desc: '热点人物排行' },
    { value: 'places', icon: MapPin, label: t('report.hotSpots'), desc: '热点地区分析' },
    { value: 'cluster', icon: Layers, label: t('report.topicClustering'), desc: '主题观点聚类分析' },
    { value: 'vranking', icon: Award, label: t('report.socialVRanking'), desc: '社交网络大V热度排名' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => { window.location.href = '/report' }}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">{t('report.viewDetail')}</h2>
        <Badge variant="secondary">{report?.report_name || `ID: ${id}`}</Badge>
      </div>

      {report && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t('report.reportType')}</p>
              <p className="mt-1 text-sm font-medium">
                {report.report_type === 1 ? t('report.dailyReport') : report.report_type === 2 ? t('report.weeklyReport') : t('report.monthlyReport')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t('report.reportPeriod')}</p>
              <p className="mt-1 text-sm font-medium">{report.start_time} ~ {report.end_time}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t('report.reportStatus')}</p>
              <Badge variant={report.report_status === 2 ? 'success' : 'warning'} className="mt-1">
                {report.report_status === 2 ? t('report.completed') : t('report.editing')}
              </Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {report?.content && (
        <Card>
          <CardContent className="p-6">
            <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">
              {report.content}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="flex w-full flex-wrap h-auto">
          {sections.map((section) => (
            <TabsTrigger key={section.value} value={section.value}>
              <section.icon className="mr-1.5 h-3.5 w-3.5" />
              {section.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {sections.map((section) => (
          <TabsContent key={section.value} value={section.value}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <section.icon className="h-5 w-5 text-muted-foreground" />
                  {section.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-3">
                  <section.icon className="h-16 w-16 opacity-20" />
                  <p>{t('common.noData')}</p>
                  <p className="text-xs opacity-60 max-w-md text-center">{section.desc}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
